import { UpdateContractor } from "@/types/contractor";
import { useTranslation } from "react-i18next";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  updateContractor as updateContractorSchema,
  updateContractorRestricted,
} from "@/schemas/contractor";
import { useContractorContext } from "../context";
import Form from "@/components/Form";
import SaveButton from "@/components/SaveButton";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { isRequired } from "@/lib/form";
import { Module } from "@/constants";
import CheckboxField from "@/components/CheckboxField";
import { SYSTEM_ROLE_CODE } from "@/schemas/user";
import { cn } from "@/lib/utils";
import { useAccountRoleCheck } from "@/modules/account/hooks/useAccountRoleCheck";

const ROLES_WITH_HIDDEN_PAYROLL_FIELDS: string[] = [
  SYSTEM_ROLE_CODE.VICE_STORE_COORDINATOR,
  SYSTEM_ROLE_CODE.STORE_COORDINATOR,
  SYSTEM_ROLE_CODE.SUPERVISOR,
];

const ROLES_WITH_RESTRICTED_PAYROLL_EDIT: string[] = [
  SYSTEM_ROLE_CODE.REGIONAL_REPRESENTATIVE,
  SYSTEM_ROLE_CODE.REGIONAL_DIRECTOR,
];

const ContractorUpdateForm = () => {
  const { t } = useTranslation();
  const { can_update } = useModulePermissions(Module.Contractors);
  const { query, handleUpdate, loadingState } = useContractorContext();
  const { hasAnyRole } = useAccountRoleCheck();

  const shouldHidePayrollFields = hasAnyRole(ROLES_WITH_HIDDEN_PAYROLL_FIELDS);
  const isPayrollEditRestricted = hasAnyRole(
    ROLES_WITH_RESTRICTED_PAYROLL_EDIT,
  );

  const form = useForm<UpdateContractor>({
    resolver: zodResolver(updateContractorSchema),
    defaultValues: query.data
      ? {
          first_name: query.data.first_name || "",
          last_name: query.data.last_name || "",
          email: query.data.email || "",
          phone_number: query.data.phone_number || "",
          payroll_comment: query.data.payroll_comment || "",
          block_agreement2: query.data.block_agreement2 || false,
          block_contests: query.data.block_contests || false,
        }
      : {
          first_name: "",
          last_name: "",
          email: "",
          phone_number: "",
          payroll_comment: "",
          block_agreement2: false,
          block_contests: false,
        },
    disabled: loadingState.isLoading || !can_update,
  });

  useFormUnsavedChangesWarning(form);

  const onSubmit: SubmitHandler<UpdateContractor> = async (data) => {
    const payload = isPayrollEditRestricted
      ? updateContractorRestricted.parse(data)
      : data;
    handleUpdate(payload);
  };

  return (
    <Form form={form} handleSubmit={onSubmit}>
      <div className="space-y-6">
        <Fieldset className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field
            name="first_name"
            label={t("contractors.fields.first_name")}
            required={isRequired(updateContractorSchema.shape.first_name)}
            render={({ field }) => <Input className="w-full" {...field} />}
          />
          <Field
            name="last_name"
            label={t("contractors.fields.last_name")}
            required={isRequired(updateContractorSchema.shape.last_name)}
            render={({ field }) => <Input className="w-full" {...field} />}
          />
          <Field
            name="email"
            label={t("contractors.fields.email")}
            required={isRequired(updateContractorSchema.shape.email)}
            render={({ field }) => <Input className="w-full" {...field} />}
          />
          <Field
            name="phone_number"
            label={t("contractors.fields.phone_number")}
            render={({ field }) => <Input className="w-full" {...field} />}
          />
        </Fieldset>

        {!shouldHidePayrollFields && (
          <Fieldset
            className={cn(
              isPayrollEditRestricted && "opacity-60 cursor-not-allowed",
            )}
          >
            <Field
              name="payroll_comment"
              label={t("contractors.fields.payroll_comment")}
              render={({ field }) => (
                <Textarea
                  className="w-full"
                  {...field}
                  placeholder={t("contractors.placeholders.payroll_comment")}
                  rows={3}
                  disabled={isPayrollEditRestricted}
                />
              )}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <CheckboxField
                name="block_agreement2"
                label={t("contractors.fields.block_agreement2")}
                disabled={isPayrollEditRestricted}
              />
              <CheckboxField
                name="block_contests"
                label={t("contractors.fields.block_contests")}
                disabled={isPayrollEditRestricted}
              />
            </div>
          </Fieldset>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <SaveButton />
      </div>
    </Form>
  );
};

export default ContractorUpdateForm;
